/**
 * Connector detail page component.
 * View connector details, capabilities, and instances.
 */

// React Imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { connectorService } from '../services/connectorService';
import { useNotificationStore } from '../store/notificationStore';
import { ConnectorIcon } from '../components/connectors/ConnectorIcon';
import { CapabilityList } from '../components/connectors/CapabilityList';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';
import type { ConnectorDetailDto, ConnectorInstanceDto } from '../types/connector';

/**
 * Connector detail page component.
 */
export const ConnectorDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();

  const [connector, setConnector] = useState<ConnectorDetailDto | null>(null);
  const [instances, setInstances] = useState<ConnectorInstanceDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConnector = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await connectorService.getConnector(id);
      setConnector(data);
      
      // Load instances
      const instanceData = await connectorService.getConnectorInstances(id);
      setInstances(instanceData);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load connector';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnector();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleCreateInstance = () => {
    if (id) {
      navigate(`/connectors/${id}/instances/new`);
    }
  };

  return (
    <PageDetailLayout
      title={connector?.name || 'Connector'}
      subtitle={connector?.vendor ? `by ${connector.vendor}` : undefined}
      breadcrumbs={[{ label: 'Connectors', href: '/connectors' }, { label: connector?.name || 'Connector' }]}
      backTo={{ label: 'Connectors', href: '/connectors' }}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      icon={connector ? <ConnectorIcon connectorKey={connector.key} category={connector.category} sx={{ color: 'primary.main' }} /> : undefined}
      status={connector ? { label: connector.isActive ? 'Active' : 'Inactive', color: connector.isActive ? 'success' : 'default' } : undefined}
      chips={connector ? [
        ...(connector.category ? [{ label: connector.category, variant: 'outlined' as const }] : []),
        { label: `v${connector.version}`, variant: 'outlined' as const },
      ] : []}
      actions={connector ? [
        { icon: <AddIcon />, label: 'Create Instance', onClick: handleCreateInstance, variant: 'contained' as const },
      ] : []}
      tabs={connector ? [
        {
          label: 'Overview',
          value: 'overview',
          content: (
            <Grid spacing={3}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Description
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  {connector.description || 'No description available.'}
                </Typography>

                <Typography variant="h6" sx={{ mb: 2 }}>
                  Key Features
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Authentication"
                      secondary={connector.authenticationType || 'None'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Capabilities"
                      secondary={`${connector.capabilities.length} total (${connector.capabilities.filter((c) => c.type === 'Action').length} actions, ${connector.capabilities.filter((c) => c.type === 'Trigger').length} triggers)`}
                    />
                  </ListItem>
                  {connector.baseUrl && (
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Base URL"
                        secondary={connector.baseUrl}
                      />
                    </ListItem>
                  )}
                  {connector.apiVersion && (
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="API Version"
                        secondary={connector.apiVersion}
                      />
                    </ListItem>
                  )}
                </List>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Quick Stats
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Capabilities
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {connector.capabilityCount}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Instances
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {connector.instanceCount}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          label={connector.isActive ? 'Active' : 'Inactive'}
                          color={connector.isActive ? 'success' : 'default'}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ),
        },
        {
          label: 'Capabilities',
          value: 'capabilities',
          content: (
            <CapabilityList
              capabilities={connector.capabilities}
              enabledCapabilityIds={connector.capabilities.filter((c) => c.isActive).map((c) => c.id)}
              readOnly
              showDetails
            />
          ),
        },
        {
          label: 'Instances',
          value: 'instances',
          content: (
            <>
              {instances.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No instances created yet.
                  </Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateInstance}>
                    Create First Instance
                  </Button>
                </Box>
              ) : (
                <Grid spacing={2}>
                  {instances.map((instance) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={instance.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { boxShadow: 4 },
                        }}
                        onClick={() => navigate(`/connectors/instances/${instance.id}`)}
                      >
                        <CardContent>
                          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                            {instance.name}
                          </Typography>
                          {instance.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {instance.description}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={instance.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={instance.isActive ? 'success' : 'default'}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          ),
        },
        {
          label: 'Usage & Analytics',
          value: 'usage',
          content: (
            <>
              <Grid spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Connector Statistics
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Total Capabilities
                          </Typography>
                          <Typography variant="h5" fontWeight={600}>
                            {connector.capabilityCount}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Active Instances
                          </Typography>
                          <Typography variant="h5" fontWeight={600}>
                            {instances.filter((i) => i.isActive).length} / {instances.length}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Actions
                          </Typography>
                          <Typography variant="h5" fontWeight={600}>
                            {connector.capabilities.filter((c) => c.type === 'Action').length}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Triggers
                          </Typography>
                          <Typography variant="h5" fontWeight={600}>
                            {connector.capabilities.filter((c) => c.type === 'Trigger').length}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Instance Overview
                      </Typography>
                      {instances.length === 0 ? (
                        <Alert severity="info">
                          No instances created yet. Create an instance to start using this connector.
                        </Alert>
                      ) : (
                        <List>
                          {instances.map((instance) => (
                            <ListItem key={instance.id}>
                              <ListItemText
                                primary={instance.name}
                                secondary={
                                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                    <Chip
                                      label={instance.isActive ? 'Active' : 'Inactive'}
                                      size="small"
                                      color={instance.isActive ? 'success' : 'default'}
                                    />
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 3 }}>
                Detailed usage analytics and trends are available for each connector instance. View an instance to see usage statistics, rate limits, and cost tracking.
              </Alert>
            </>
          ),
        },
      ] : []}
    />
  );
};
